import React, { useContext, useState, useEffect } from 'react';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import CloseIcon from '@mui/icons-material/Close';
import { BulkActionProps } from './props.interface';
import {
  FormControl,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Typography,
  Tooltip,
} from '@mui/material';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { AuthContext } from '../../../contexts/user-context/user-context';
import { isMobile, isTablet } from 'react-device-detect';
import { OrderStickers } from '../waybill-printer/waybill-printer-minified';
import { OrderType } from '../../../interfaces/delivery-company/orders';
import axios from 'axios';
import { API_ENDPOINT } from '../../../configurations/global.config';
import { useSnackBar } from '../../../contexts/snackbar';
import { UserType } from '../../../enums/users';
import { ThermalPrintSection } from './thermal-print-section';
import { isWebBluetoothSupported } from '../../../hooks/use-web-bluetooth';


interface PrintWaybillsMultiSizesProps {
  orders: OrderType[]
  pickupTaskId?: number | string
  timestamp?: string | null  // Optional timestamp to filter orders
  onClose?: ()=>void;
}

export function PrintWaybillsMultiSizes({ orders, pickupTaskId, timestamp, onClose }: PrintWaybillsMultiSizesProps) {
  const fileName = 'waybills';
  const { user, config } = useContext(AuthContext);
  const [mode, setMode] = useState<'stickers' | 'pages' | 'thermal'>( user?.type == UserType.MERCHANT ? 'pages' : 'stickers');
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [showPdfPreview, setShowPdfPreview] = useState(false);

  const { showMessage } = useSnackBar();
  const orgId = user?.organisation?.org_id ?? 'UNKNOWN';
  const isThermalSupported = isWebBluetoothSupported();

  // Cleanup blob URL when component unmounts or PDF changes
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        window.URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  const downloadFromBackend = async () => {
    try {
      setLoading(true);

      let response;

      if (pickupTaskId) {
        // Build URL with optional timestamp parameter
        let url = `${API_ENDPOINT}/v1/pickup-task/${pickupTaskId}/print-waybills/?mode=A4`;
        
        // Add timestamp parameter if provided
        if (timestamp !== undefined && timestamp !== 'all') {
          if (timestamp === 'null' || timestamp === null) {
            url += '&timestamp=';  // Empty string for null/legacy orders
          } else {
            url += `&timestamp=${encodeURIComponent(timestamp)}`;
          }
        }
        
        response = await axios.get(
          url,
          {
            ...config,
            responseType: 'blob',
            headers: {
              Accept: '*/*',
              "Content-Type": "application/json",
              ...(config?.headers ?? {}),         // merge your existing headers
            },
          }
        );
      } else if (orders) {
        const orderIds = orders.map((o) => o.id);

        response = await axios.post(
          `${API_ENDPOINT}/v1/orders/print-waybills/`,
          { order_ids: orderIds },
          { ...config, responseType: 'blob' } // important: get PDF as blob
        );
      } 

      if (!response) {
        throw new Error("Failed")
      }
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Clean up previous URL if exists
      if (pdfUrl) {
        window.URL.revokeObjectURL(pdfUrl);
      }
      
      // Show PDF preview dialog instead of downloading immediately
      setPdfUrl(url);
      setShowPdfPreview(true);
      showMessage("PDF loaded successfully", "success")
    } catch (err) {
      console.error('Failed to load PDF:', err);
      showMessage("Failed to load PDF", "error")
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (!pdfUrl) return;
    
    // Open PDF in new window and trigger print dialog
    const printWindow = window.open(pdfUrl, '_blank');
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  };

  const handleDownload = () => {
    if (!pdfUrl) return;
    
    const a = document.createElement('a');
    a.href = pdfUrl;
    a.download = `${fileName}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    showMessage("PDF downloaded successfully", "success");
  };

  const handleClosePdfPreview = () => {
    setShowPdfPreview(false);
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
    onClose?.();
  };

  return (
    <>
      <FormControl fullWidth>
        <RadioGroup
          row
          style={{ textAlign: 'center', display: 'flex', justifyContent: 'center' }}
          onChange={(e) => setMode(e.target.value as 'stickers' | 'pages' | 'thermal')}
          value={mode}
        >
          <FormControlLabel
            checked={mode === 'stickers'}
            value={'stickers'}
            control={<Radio />}
            label="Mini Stickers"
          />
          <FormControlLabel
            checked={mode === 'pages'}
            value={'pages'}
            control={<Radio />}
            label="A4 Waybills"
          />
          <Tooltip
            title={
              !isThermalSupported
                ? 'Thermal printing requires Chrome or Edge browser'
                : ''
            }
            arrow
          >
            <span>
              <FormControlLabel
                checked={mode === 'thermal'}
                value={'thermal'}
                control={<Radio />}
                label="Thermal Printer"
                disabled={!isThermalSupported}
              />
            </span>
          </Tooltip>
        </RadioGroup>

        {mode === 'thermal' ? (
          <Box sx={{ mt: 2 }}>
            <ThermalPrintSection orders={orders} />
          </Box>
        ) : (
          <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            {mode === 'stickers' ? (
              <PDFDownloadLink
                document={<OrderStickers orgId={orgId} orders={orders} />}
                fileName={fileName}
              >
                {({ loading }) =>
                  loading ? (
                    <CircularProgress />
                  ) : (
                    <Button
                      size={isMobile && !isTablet ? 'small' : 'medium'}
                      color="primary"
                    >
                      <DownloadIcon fontSize="large" />
                    </Button>
                  )
                }
              </PDFDownloadLink>
            ) : (
              <Button
                size={isMobile && !isTablet ? 'small' : 'medium'}
                color="primary"
                onClick={downloadFromBackend}
                disabled={loading}
              >
                {loading ? 'Loading...' : <DownloadIcon fontSize="large" />}
              </Button>
            )}
          </div>
        )}
      </FormControl>

      {/* PDF Preview Dialog */}
      <Dialog
        open={showPdfPreview}
        onClose={handleClosePdfPreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Waybills Preview</Typography>
            <IconButton onClick={handleClosePdfPreview} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          {pdfUrl ? (
            <iframe
              src={pdfUrl}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                flexGrow: 1,
              }}
              title="PDF Preview"
            />
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 1, gap: 1 }}>
          <Button
            onClick={handleDownload}
            startIcon={<DownloadIcon />}
            variant="outlined"
            color="primary"
          >
            Save to device
          </Button>
          <Button
            onClick={handlePrint}
            startIcon={<PrintIcon />}
            variant="contained"
            color="primary"
          >
            Print
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function PrintWaybills({ selectedOrders, handleClose }: BulkActionProps) {
  return (
    <PrintWaybillsMultiSizes orders={Array.from(selectedOrders.values())} onClose={handleClose} />
  );
}

export default PrintWaybills;
